import React from 'react'
import { BrowserRouter as Router, Redirect, Route } from 'react-router-dom'
import {
  CallHeaderActions,
  AdvancedCaseloadProvider,
  AdvancedModeProvider,
  AdvancedRoutes,
  HostSlotsProvider,
  useShowAdvancedChrome,
} from '@advanced'
import { AppRefresher, useNightlyRefresh } from 'src/appRefresher'
import { Communities } from 'src/communities'
import { AppBars } from 'src/designSystem'
import { FeatureFlagProvider } from 'src/featureFlags'
import { ForceChromeBrowser } from 'src/forceBrowser'
import { NavigationAndTabEvents } from 'src/mixpanel'
import { ParticipantsView } from 'src/participantsView'
import { Login, Logout, RedirectFromLogin, RedirectToLogin, SessionManager, useSession } from 'src/session'
import ParticipantCallHeader from 'src/videoCall/ParticipantCallHeader'

import Admin from 'src/legacy/components/Admin'
import { encodeObjectForWaf } from 'src/lib/wafEncoding'
import { startBrowserNoteDraftMigration } from 'src/noteDraft/migrateBrowserNoteDrafts'
import { useImportNoteDraftMutation } from 'src/noteDraft/noteDraft.generated'
import useLocalStorageCleanUp from './legacy/lib/useLocalStorageCleanUp'
import MainView from './legacy/containers/MainView'

const standardHomeRoutes = (
  <>
    <Route exact path="/" render={() => <Redirect to="/participant" />} />
    <Route path="/user/:uid" render={({ match }) => <Redirect to={`/participant/${match.params.uid}`} />} />
    <Route component={MainView} path={['/participant/:uid/', '/participant']} />
    <Route path="/advanced/:uid" render={({ match }) => <Redirect to={`/participant/${match.params.uid}`} />} />
    <Route exact path="/advanced" render={() => <Redirect to="/participant" />} />
    <Route exact path="/advanced-config" render={() => <Redirect to="/participant" />} />
  </>
)

const sharedRoutes = (
  <>
    <Route component={ParticipantsView} path="/participants" />
    <Route component={Communities} path="/communities/:postID?" />
    <Route component={Admin} path="/admin" />
  </>
)

const advancedHostSlots = {
  renderCallHeader: (uid: string, { onOpenCallNotes, showForProgram }: CallHeaderActions) => (
    <ParticipantCallHeader uid={uid} onOpenCallNotes={onOpenCallNotes} showForProgram={showForProgram} />
  ),
}

const LoggedInRoutes = () =>
  useShowAdvancedChrome() ? (
    <AdvancedRoutes>{sharedRoutes}</AdvancedRoutes>
  ) : (
    <AppBars>
      {standardHomeRoutes}
      {sharedRoutes}
    </AppBars>
  )

const App = () => {
  const { isLoggedIn } = useSession()
  const [importNoteDraft] = useImportNoteDraftMutation()
  useNightlyRefresh()
  useLocalStorageCleanUp()

  if (isLoggedIn) {
    startBrowserNoteDraftMigration(async (draft) => {
      const result = await importNoteDraft({
        variables: { uid: draft.uid, type: draft.type, value: encodeObjectForWaf(draft.value) },
      })
      return result.data?.importNoteDraft.imported === true
    })
  }

  return (
    <Router>
      {isLoggedIn ? <RedirectFromLogin /> : <RedirectToLogin />}

      {isLoggedIn && (
        <FeatureFlagProvider>
          <AdvancedModeProvider>
            <AdvancedCaseloadProvider>
              <HostSlotsProvider value={advancedHostSlots}>
                <LoggedInRoutes />
              </HostSlotsProvider>
            </AdvancedCaseloadProvider>
          </AdvancedModeProvider>
        </FeatureFlagProvider>
      )}

      <Route component={Login} path="/login" />
      <Route component={Logout} path="/logout" />

      <AppRefresher refreshInterval={5 * 60 * 1000} />
      <ForceChromeBrowser />
      <NavigationAndTabEvents />
      <SessionManager />
    </Router>
  )
}

export default App
